import { useNavigate } from "react-router-dom";
import {
  Lock, Eye, Database, Trash2, MapPin, Server, Mail, Shield,
} from "lucide-react";
import { C, DISPLAY, BODY, MONO, Reveal, EditorialLabel, EditorialHeader, Section, Heading, EBtn } from "@/styles/editorial";

const SECTIONS = [
  {
    icon: Lock, color: C.orange, title: "Qué datos recogemos",
    items: [
      "Cuenta: email, nombre (solo si te registras). Puedes usar la app sin registro.",
      "Ubicación: solo cuando TÚ la activas para geolocalizar centros de rescate. No la almacenamos.",
      "Fotos de fauna: se almacenan de forma segura para los reportes y se comparten solo con el centro de rescate asignado.",
      "Plan de emergencia: datos de tus animales, contactos y puntos de encuentro. Solo tú los ves.",
    ],
  },
  {
    icon: Eye, color: C.blue, title: "Qué NO hacemos",
    items: [
      "NO vendemos tus datos. Nunca. A nadie.",
      "NO usamos cookies de rastreo ni analytics de terceros.",
      "NO compartimos tu información con empresas privadas.",
      "NO almacenamos tu ubicación GPS. Se usa en tiempo real y se descarta.",
      "NO creamos perfiles de comportamiento ni segmentos publicitarios.",
    ],
  },
  {
    icon: MapPin, color: C.olive, title: "Geolocalización",
    items: [
      "La geolocalización es OPCIONAL. La app funciona sin ella.",
      "Se usa SOLO para calcular qué centro de rescate de fauna está más cerca de ti.",
      "El cálculo se hace en tu navegador. Tu posición no viaja a nuestros servidores.",
      "Puedes revocar el permiso de ubicación en cualquier momento desde tu navegador.",
    ],
  },
  {
    icon: Database, color: C.lilac, title: "Almacenamiento",
    items: [
      "Tus datos se almacenan en servidores seguros con encriptación.",
      "Las fotos de fauna se guardan en almacenamiento cifrado con acceso restringido.",
      "Los planes de emergencia son privados y solo accesibles con tu sesión activa.",
      "No mantenemos copias de datos eliminados más allá del periodo técnico necesario.",
    ],
  },
  {
    icon: Trash2, color: C.red, title: "Tu derecho al olvido",
    items: [
      "Puedes solicitar la eliminación total de tu cuenta y datos en cualquier momento.",
      "Puedes borrar reportes de fauna individuales desde la app.",
      "Puedes eliminar tu plan de emergencia sin borrar tu cuenta.",
      "Escríbenos a privacidad@emergenciaanimal.com y lo gestionamos en 48h.",
    ],
  },
  {
    icon: Server, color: C.navy, title: "Servicios de terceros",
    items: [
      "Open-Meteo: datos meteorológicos públicos. No enviamos datos personales.",
      "Resend: envío de emails a centros de rescate. Solo se comparte la info del reporte de fauna.",
      "Telegram/WhatsApp: canales públicos. Tu participación es voluntaria.",
      "Emergent Object Storage: almacenamiento de fotos con acceso restringido.",
    ],
  },
];

export default function ProteccionDatosPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: C.white }} data-testid="proteccion-datos-page">
      <EditorialHeader backLabel="Volver al Hub" backTo="/" />

      {/* Hero */}
      <section className="px-6 md:px-12 pt-16 pb-20 md:pt-24 md:pb-28" style={{ background: C.navy }}>
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-8">
          <div className="md:col-span-7">
            <Reveal>
              <EditorialLabel number="00" text="PRIVACIDAD" color={C.orange} />
              <h1
                className="text-[clamp(2.5rem,6vw,4.5rem)] leading-[1.05] tracking-tight mb-6"
                style={{ fontFamily: DISPLAY, color: C.white }}
              >
                Protección<br />de datos
              </h1>
              <p className="text-lg md:text-xl max-w-xl" style={{ fontFamily: BODY, color: "rgba(255,255,255,0.8)", lineHeight: 1.7 }}>
                Emergencia Animal es una plataforma gratuita sin ánimo de lucro.
                No tenemos ningún interés en tus datos personales más allá de lo
                estrictamente necesario para que la plataforma funcione y salve vidas animales.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-5 flex items-center justify-center">
            <Reveal delay={0.2}>
              <div className="w-28 h-28 md:w-36 md:h-36 flex items-center justify-center" style={{ background: C.blue, borderRadius: 4 }}>
                <Shield className="w-14 h-14 md:w-20 md:h-20" style={{ color: C.white }} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TL;DR strip */}
      <section className="px-6 md:px-12 py-8" style={{ background: C.navy, borderTop: `1px solid rgba(255,255,255,0.1)` }}>
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase mb-5" style={{ fontFamily: MONO, color: C.orange }}>
              TL;DR PARA LOS QUE VAN CON PRISA
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: Lock, text: "No vendemos datos", sub: "Ni ahora ni nunca" },
                { icon: MapPin, text: "GPS opcional", sub: "No almacenamos tu ubicación" },
                { icon: Trash2, text: "Derecho al olvido", sub: "Borra todo cuando quieras" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <item.icon className="w-5 h-5 flex-shrink-0" style={{ color: C.orange }} />
                  <div>
                    <p className="text-sm font-bold" style={{ fontFamily: BODY, color: C.white }}>{item.text}</p>
                    <p className="text-xs" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.5)" }}>{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Sections */}
      <Section bg={C.white}>
        <div className="max-w-[960px]">
          {SECTIONS.map((section, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="mb-12 last:mb-0" data-testid={`privacy-section-${i}`}>
                {/* Section heading row */}
                <div className="flex items-center gap-4 mb-5">
                  <div
                    className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                    style={{ background: section.color, borderRadius: 4 }}
                  >
                    <section.icon className="w-5 h-5" style={{ color: C.white }} />
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-xs font-semibold tracking-widest" style={{ fontFamily: MONO, color: section.color }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="block w-8 h-px" style={{ background: section.color }} />
                    <h2 className="text-xl md:text-2xl leading-tight" style={{ fontFamily: DISPLAY, color: C.navy }}>
                      {section.title}
                    </h2>
                  </div>
                </div>

                {/* Items */}
                <div className="pl-14">
                  <ul className="space-y-3">
                    {section.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm md:text-base" style={{ fontFamily: BODY, color: C.navy, lineHeight: 1.7 }}>
                        <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: section.color }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Divider */}
                {i < SECTIONS.length - 1 && (
                  <div className="mt-10 h-px" style={{ background: `${C.navy}10` }} />
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Contact CTA */}
      <section className="px-6 md:px-12 py-16 md:py-24" style={{ background: C.navy }}>
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <Reveal>
              <EditorialLabel text="CONTACTO" color={C.orange} />
              <Heading size="md" className="mb-4" style={{ color: C.white }}>
                <span style={{ color: C.white }}>
                  ¿Dudas sobre tus datos?
                </span>
              </Heading>
              <p className="text-base md:text-lg" style={{ fontFamily: BODY, color: "rgba(255,255,255,0.7)", lineHeight: 1.7 }}>
                Escríbenos para ejercer tu derecho al olvido o cualquier consulta sobre privacidad.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-4">
            <Reveal delay={0.15}>
              <div className="flex items-center gap-3 mb-2">
                <Mail className="w-5 h-5" style={{ color: C.orange }} />
                <p className="text-sm font-bold" style={{ fontFamily: BODY, color: C.orange }}>EMAIL</p>
              </div>
              <p className="text-base font-semibold" style={{ fontFamily: MONO, color: C.white }}>
                privacidad@emergenciaanimal.com
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Tagline strip */}
      <div className="px-6 py-4" style={{ background: C.olive }}>
        <p className="text-center text-xs font-semibold tracking-[0.2em] uppercase" style={{ fontFamily: MONO, color: C.white }}>
          EMERGENCIA ANIMAL — TU PRIVACIDAD ES SAGRADA
        </p>
      </div>
    </div>
  );
}
